import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { Confirm, Icons, Modal, Table } from '@components/common';

import { useMutateMavenirConfigDelete } from '@hooks/dataSources/useMutateMavenirConfigDelete';
import { useMutateMavenirConfigExport } from '@hooks/dataSources/useMutateMavenirConfigExport';
import { useMutateMavenirConfigsDelete } from '@hooks/dataSources/useMutateMavenirConfigsDelete';
import { useMutateMavenirConfigsExport } from '@hooks/dataSources/useMutateMavenirConfigsExport';
import useQueryMavenirConfigDatesOptions from '@hooks/dataSources/useQueryMavenirConfigDatesOptions';
import useQueryMavenirConfigIdOptions from '@hooks/dataSources/useQueryMavenirConfigIdOptions';
import useQueryMavenirConfigs from '@hooks/dataSources/useQueryMavenirConfigs';
import useQueryMavenirConfigTypeOptions from '@hooks/dataSources/useQueryMavenirConfigTypeOptions';
import useQueryMavenirConfigVersionOptions from '@hooks/dataSources/useQueryMavenirConfigVersionOptions';

import { dataSourceGNBConfiguration } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setConfigId,
	setConfigType,
	setDate,
	setPage,
	setPageSize,
	setReleaseVersion,
	setSort,
} from '@slices/dataSourceGNBConfiguration';

import {
	formatDate,
	startOfDayInMilliseconds,
} from '@utils/date';
import { setFilterUtil } from '@utils/filterUtil';

import { IMavenirConfigInfo } from '@interfaces/cmModule/dataSources';
import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITableDatepickerFilter,
	ITablePagination,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';

import { defaultMeta, TMenuActionsTypes } from '@enums/common';

import { MODAL_WIDTH_580 } from '@constants/modals';

import AttachMavenirConfig from './AttachMavenirConfig';
import LocalCellIdsList from './LocalCellIdsList';
import MavenirConfigTree from './MavenirConfigTree';
import { getColumns, getDateFilters, getFilters } from './utils';

import styles from './DataSourceGNBConfigurationTable.module.scss';

const DataSourceGNBConfigurationTable = () => {
	const dispatch = useAppDispatch();

	const {
		sortDirection,
		sortFieldName,
		page,
		pageSize,
		configId,
		type,
		date,
		gnbId,
		version,
	} = useAppSelector(dataSourceGNBConfiguration);

	const dateText = useMemo(() => {
		return date
			? formatDate(date)
			: null;
	}, [date]);

	const [selectedConfig, setSelectedConfig] = useState<IMavenirConfigInfo | null>(null);
	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();

	const [selectedConfigId, setSelectedConfigId] = useState<TSelectOption<string>>(null);
	const [selectedConfigType, setSelectedConfigType] = useState<TSelectOption<string>>(null);
	const [selectedConfigVersion, setSelectedConfigVersion] = useState<TSelectOption<string>>(null);
	const [localIdsList, setLocalIdsList] = useState<number[]>([]);

	const [isOpenUploadFilesModal, setIsOpenUploadFilesModal] = useState(false);
	const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
	const [isConfigTree, setIsMavenirConfigTree] = useState(false);
	const [isDirtyUploadForm, setIsDirtyUploadForm] = useState(false);

	const toggleUploadFilesModal = useCallback(() => {
		setIsOpenUploadFilesModal(prevState => !prevState);
		setIsDirtyUploadForm(false);
	}, []);

	const openConfigTree = useCallback(() => setIsMavenirConfigTree(true), []);
	const closeConfigTree = useCallback(() => {
		setIsMavenirConfigTree(false);
		setSelectedConfig(null);
	}, []);

	const openDeleteConfirm = useCallback(() => setIsDeleteConfirm(true), []);
	const closeDeleteConfirm = useCallback(() => {
		setIsDeleteConfirm(false);
		setSelectedConfig(null);
	}, []);

	const closeViewCellIdsModal = useCallback(() => setLocalIdsList([]), []);

	const { versionOptions, latestVersion } = useQueryMavenirConfigVersionOptions();
	const { datesOptions, latestDateText } = useQueryMavenirConfigDatesOptions();
	const { typeOptions } = useQueryMavenirConfigTypeOptions();
	const { idsOptions } = useQueryMavenirConfigIdOptions();

	const gnbSortDirection = sortFieldName === 'gnbId' ? sortDirection : null;

	const {
		mavenirConfigs,
		isLoadingMavenirConfigs,
		countOfRecords,
	} = useQueryMavenirConfigs({
		page,
		pageSize,
		sortFieldName: gnbSortDirection ? null : sortFieldName,
		sortDirection: gnbSortDirection ? null : sortDirection,
		configId,
		type,
		dateText,
		version,
		gnbId,
		gnbSortDirection,
	});

	const groupOperationConfigsFilter = useMemo(() => {
			return {
				configId,
				type,
				dateText,
				gnbId,
				version,
			};
		},
		[
			configId,
			type,
			dateText,
			gnbId,
			version,
		]);

	const { exportConfig } = useMutateMavenirConfigExport();
	const { deleteConfig, isLoadingDeleteMavenirConfig } = useMutateMavenirConfigDelete(closeDeleteConfirm);
	const { deleteConfigs, isLoadingDeleteConfigs } = useMutateMavenirConfigsDelete({
		filter: groupOperationConfigsFilter,
		onSuccess: closeDeleteConfirm,
	});
	const { exportConfigs } = useMutateMavenirConfigsExport({
		filter: groupOperationConfigsFilter,
	});

	const handleExportConfigs = useCallback((ids: string[], actionType?: TMenuActionsTypes) => {
		return exportConfigs({ ids, actionType });
	}, [exportConfigs]);

	const handleExportConfig = useCallback((id?: string) => {
		if (!id) return;

		exportConfig(id);
	}, [exportConfig]);

	const columns = useMemo(() => getColumns({
		setSelectedConfig,
		openConfigTree,
		openDeleteConfirm,
		handleExportConfig,
		setLocalIdsList,
	}), [
		setSelectedConfig,
		openConfigTree,
		handleExportConfig,
		openDeleteConfirm,
		setLocalIdsList,
	]);

	const onChangeDate = useCallback((value: number | null) => {
		dispatch(setDate({ date: value }));
	}, [dispatch]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const handleDeleteConfig = useCallback(async () => {
		if (selectedConfig?.id) return deleteConfig(selectedConfig?.id);

		try {
			await deleteConfigs({
				ids: checkedIds,
				actionType: actionId,
			});

			closeDeleteConfirm();

			pageHandler(defaultMeta.page);
		} catch {
			return;
		}
	}, [
		closeDeleteConfirm,
		pageHandler,
		selectedConfig,
		checkedIds,
		actionId,
		deleteConfig,
		deleteConfigs,
	]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const dateFilters: ITableDatepickerFilter[] = useMemo(() => {
		return getDateFilters({
			onChangeDate,
			date: date ? new Date(date) : null,
			datesOptions,
		});
	}, [
		onChangeDate,
		date,
		datesOptions,
	]);

	useEffect(() => {
		if (!versionOptions?.length) {
			setSelectedConfigVersion(null);

			return;
		}

		if (!latestVersion || !!version) return;

		setSelectedConfigVersion(latestVersion);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [latestVersion?.value]);

	useEffect(() => {
		if (!datesOptions?.length) {
			dispatch(setDate({ date: null }));

			return;
		}

		if (!latestDateText || !!date) return;

		const lastDate = startOfDayInMilliseconds(latestDateText);

		dispatch(setDate({ date: lastDate }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [latestDateText]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedConfigType,
			currentValue: type,
			setAction: setConfigType,
			fieldName: 'type',
			dispatch,
		});
	}, [dispatch, type, selectedConfigType]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedConfigId,
			currentValue: configId,
			setAction: setConfigId,
			fieldName: 'configId',
			dispatch,
		});
	}, [dispatch, configId, selectedConfigId]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedConfigVersion,
			currentValue: version,
			setAction: setReleaseVersion,
			fieldName: 'version',
			dispatch,
		});
	}, [dispatch, version, selectedConfigVersion]);

	const filters: TTableFilter[] = useMemo(() => {
		return getFilters({
			setSelectedConfigId,
			setSelectedConfigVersion,
			setSelectedConfigType,
			selectedConfigVersion,
			selectedConfigId,
			selectedConfigType,
			idsOptions,
			typeOptions,
			versionOptions,
			configId,
			type,
			gnbId,
			version,
		});
	}, [
		setSelectedConfigType,
		setSelectedConfigVersion,
		setSelectedConfigId,
		selectedConfigType,
		selectedConfigVersion,
		selectedConfigId,
		idsOptions,
		typeOptions,
		versionOptions,
		configId,
		type,
		gnbId,
		version,
	]);

	const handleCustomDelete = useCallback((data: IGroupOperationWithActionDTO) => {
		openDeleteConfirm();
		setCheckedIds(data.ids.filter(Boolean));
		setActionId(data.actionType);
	}, [openDeleteConfirm]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => {
		return {
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		};
	}, [sortFieldName, sortDirection, handleSort]);

	const getTreeModalTitle = useCallback(() => {
		const { type, version = '', gnbId = '', configId = '' } = selectedConfig || {};

		return [gnbId, version, type?.name || '', configId].filter(Boolean).join(' - ');
	}, [selectedConfig]);

	return (
		<div className={styles.wrapper}>
			{selectedConfig?.id && (
				<Modal
					height='80vh'
					isOpen={!!(isConfigTree && selectedConfig)}
					onClose={closeConfigTree}
					title={getTreeModalTitle()}
					width='700px'
				>
					<MavenirConfigTree
						id={selectedConfig?.id}
						onClose={closeConfigTree}
					/>
				</Modal>
			)}
			<Modal
				isLoading={isLoadingDeleteConfigs}
				isOpen={isDeleteConfirm}
				onClose={closeDeleteConfirm}
			>
				<Confirm
					isLoading={isLoadingDeleteMavenirConfig}
					onSubmit={handleDeleteConfig}
					question='Are you sure you want to delete the CM data?'
				/>
			</Modal>
			{!!localIdsList.length && (
				<Modal
					isOpen={!!localIdsList.length}
					onClose={closeViewCellIdsModal}
					title='Cell local ID list'
					width='400px'
				>
					<LocalCellIdsList
						cellIds={localIdsList}
						onClose={closeViewCellIdsModal}
					/>
				</Modal>
			)}
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyUploadForm}
				isOpen={isOpenUploadFilesModal}
				isScrollableContent
				onClose={toggleUploadFilesModal}
				title='Upload file'
				width={MODAL_WIDTH_580}
			>
				<AttachMavenirConfig
					onClose={toggleUploadFilesModal}
					setIsDirtyForm={setIsDirtyUploadForm}
				/>
			</Modal>
			<Table
				addIcon={Icons.import}
				addText='Upload'
				addTooltipText='Upload'
				collapseTriggerNames={['gnbId']}
				columns={columns}
				data={mavenirConfigs}
				dateFilters={dateFilters}
				exportTooltipText='Export'
				filters={filters}
				handleCustomDelete={handleCustomDelete}
				handleExport={handleExportConfigs}
				isClearableDatepicker={false}
				isCreatable
				isEditAccess
				isExport
				isLoading={isLoadingMavenirConfigs}
				isOpenFilters
				pagination={pagination}
				setCheckedIds={setCheckedIds}
				sort={sort}
				toggleAddModal={toggleUploadFilesModal}
			/>
		</div>
	);
};

export default memo(DataSourceGNBConfigurationTable);
